import { useMemo } from 'react';
import { useGoBack } from '@/hooks/useGoBack';
import { AlertTriangle, ArrowLeft, CheckCircle2, Download } from 'lucide-react';

import { SoftCard } from '@/components/ui/soft-card';
import { Skeleton } from '@/components/ui/skeleton';
import { useShop } from '@/hooks/useShop';

import { useInventoryPageSummary, useUploadHistory } from '@/hooks/useInventory';
import { DISCONNECTED_THRESHOLD_DAYS, daysSince } from '@/lib/connection-state';
import myShopClient from '@services/nearlive';

import { UptimeBar } from './components/UptimeBar';
import { StockUploadHistory } from './components/StockUploadHistory';
import { StockAccuracyCard } from './components/StockAccuracyCard';
import { buildSeries } from './build-series';

/**
 * Four real states the Inventory accuracy page's Status line can be in
 * (NEA-5942). Returned as a tagged kind so the call site and tests speak
 * the same names.
 *
 * `no-source`      — no POS source row at all (a freshly created shop that
 *                    hasn't been connected yet, or a shop whose source was
 *                    just deleted).
 * `awaiting`       — source row exists but no ingest has landed yet
 *                    (post-connect, pre-first-upload).
 * `stale`          — source row + lastSyncAt, but the ingest is ≥
 *                    `DISCONNECTED_THRESHOLD_DAYS` old. Carries the day count.
 * `receiving`      — source row + lastSyncAt within the threshold.
 *
 * The old page collapsed all four into "We are receiving stock updates",
 * which lied in the first three. Lifted to a pure helper so the four-way
 * branch is table-testable.
 */
export function deriveInventoryConnectionStatus(source) {
	if (!source) return { kind: 'no-source' };
	if (!source.lastSyncAt) return { kind: 'awaiting' };
	const days = daysSince(source.lastSyncAt);
	if (days != null && days >= DISCONNECTED_THRESHOLD_DAYS) return { kind: 'stale', days };
	return { kind: 'receiving' };
}

function formatRelativeTime(iso) {
	if (!iso) return null;
	const t = new Date(iso).getTime();
	if (!Number.isFinite(t)) return null;
	const minutes = Math.floor((Date.now() - t) / 60000);
	if (minutes < 1) return 'just now';
	if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
	const hours = Math.floor(minutes / 60);
	if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
	const days = Math.floor(hours / 24);
	return `${days} day${days === 1 ? '' : 's'} ago`;
}

export function InventoryConnection() {
	const goBack = useGoBack('/inventory');
	const { shop, mutate: mutateShop } = useShop();
	const { summary, isLoading: summaryLoading } = useInventoryPageSummary(shop?.id);
	const { rows, isLoading: historyLoading } = useUploadHistory(shop?.id);

	const { days, chart } = useMemo(() => buildSeries(rows), [rows]);

	const source = summary?.source ?? null;
	const lastSync = formatRelativeTime(source?.lastSyncAt);
	const status = deriveInventoryConnectionStatus(source);
	const isStale = status.kind === 'stale';

	const downloadLastUpload = async () => {
		if (!shop?.id || !source?.id) return;
		try {
			const client = await myShopClient();
			const { data } = await client.get(`/api/inventory-file/${shop.id}/${source.id}`);
			if (data?.url) window.open(data.url, '_blank', 'noopener');
		} catch (e) {
			window.alert(`Could not fetch the last upload: ${e?.response?.data?.message ?? e.message}`);
		}
	};

	return (
		<div className="space-y-8">
			<div>
				<button
					type="button"
					onClick={goBack}
					className="mb-3 inline-flex cursor-pointer items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground transition-colors hover:text-foreground"
				>
					<ArrowLeft className="h-3 w-3" /> Back
				</button>
				<h1 className="text-3xl font-extrabold tracking-tight md:text-[32px]">Inventory accuracy</h1>
				<p className="mt-2 text-muted-foreground">
					Where your stock comes from, how reliably we receive it, and your confidence in the accuracy.
				</p>
			</div>

			<div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
				<SoftCard className="space-y-5 !p-5 md:!p-6">
					<div className="flex items-center justify-between gap-4">
						<h3 className="text-[15px] font-bold text-foreground md:text-base">Connection uptime</h3>
						{source?.id ? (
							<button
								type="button"
								onClick={downloadLastUpload}
								className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-muted px-3 py-1.5 text-sm font-semibold text-foreground transition hover:bg-accent"
							>
								Last upload <Download className="h-3.5 w-3.5" />
							</button>
						) : null}
					</div>

					{summaryLoading ? (
						<Skeleton className="h-9 w-48" />
					) : (
						<div className="flex flex-wrap items-center gap-4">
							<div
								className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
									isStale ? 'bg-warning-soft' : 'bg-success-soft'
								}`}
							>
								{isStale ? (
									<AlertTriangle className="h-5 w-5 text-warning-soft-foreground" />
								) : (
									<CheckCircle2 className="h-5 w-5 text-success-soft-foreground" />
								)}
							</div>
							<div className="font-bold text-foreground">{source?.label ?? 'No source connected'}</div>
							<div className="text-sm text-foreground">Stock data</div>
							{lastSync ? <div className="text-sm text-muted-foreground">{lastSync}</div> : null}
						</div>
					)}

					{historyLoading ? <Skeleton className="h-7 w-full" /> : <UptimeBar days={days} />}

					<div className="border-t border-border/60 pt-4">
						<h4 className="mb-2 text-sm font-bold text-foreground">Status</h4>
						{status.kind === 'no-source' ? (
							<p className="text-sm text-muted-foreground">No source connected to this location yet.</p>
						) : status.kind === 'awaiting' ? (
							<p className="text-sm text-muted-foreground">
								Awaiting first sync — your POS hasn&rsquo;t uploaded any stock yet.
							</p>
						) : status.kind === 'stale' ? (
							<p className="text-sm text-warning-soft-foreground">
								We haven&rsquo;t received a stock update for this location in {status.days} days — check your POS
								connection.
							</p>
						) : (
							<p className="text-sm text-foreground">We are receiving stock updates for this location.</p>
						)}
						{summary?.totalItems != null ? (
							<p className="text-sm text-foreground">
								Your inventory has <span className="tabular-nums">{summary.totalItems.toLocaleString()}</span> items (
								<span className="tabular-nums">{(summary.inStockItems ?? 0).toLocaleString()}</span> in stock).
							</p>
						) : null}
					</div>
				</SoftCard>

				{historyLoading ? <Skeleton className="h-[260px] w-full rounded-2xl" /> : <StockUploadHistory data={chart} />}
			</div>

			<div className="pt-4">
				<div className="mb-5">
					<div className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
						Stock confidence
					</div>
					<h2 className="text-xl font-bold text-foreground md:text-[22px]">Improve your stock accuracy</h2>
					<p className="mt-1 text-sm text-muted-foreground">
						Stock confidence is how closely your system matches what's actually on your shelves. When local customers can rely
						on your availability, they come back again.
					</p>
				</div>
				<StockAccuracyCard
					shopId={shop?.id}
					organisationId={shop?.organisation}
					currentBuffer={shop?.quantityBuffer ?? 0}
					onSaved={mutateShop}
				/>
			</div>
		</div>
	);
}
